import React, { useState, useEffect, useMemo } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import useReps from '../../../hooks/useReps';

const DEFAULT_STAGES = [
  { id: 1, name: 'Prospecting' },
  { id: 2, name: 'Qualification' },
  { id: 3, name: 'Proposal' },
  { id: 4, name: 'Negotiation' },
  { id: 5, name: 'Closed Won' },
  { id: 6, name: 'Closed Lost' },
];

export default function OpportunitiesFormModal({ isOpen, opportunity, stages, onClose, onSubmit }) {
  const stageOptions = useMemo(
    () => (Array.isArray(stages) && stages.length ? stages : DEFAULT_STAGES),
    [stages],
  );

  const reps = useReps();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    value: '',
    probability: '',
    stage: 1,
    rep: '',
    closeDate: '',
    source: 'Website',
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (opportunity) {
      setFormData({
        ...opportunity,
        stage: Number(opportunity?.stage ?? 1),
      });
    } else {
      setFormData({
        name: '',
        company: '',
        value: '',
        probability: '',
        stage: 1,
        rep: '',
        closeDate: '',
        source: 'Website',
      });
    }
    setErrors({});
  }, [opportunity, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Opportunity name is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.value || formData.value <= 0) newErrors.value = 'Valid deal value required';
    if (!formData.probability || formData.probability < 0 || formData.probability > 100) {
      newErrors.probability = 'Probability must be 0-100%';
    }
    if (!formData.closeDate) newErrors.closeDate = 'Close date is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(formData);
    } catch {
      // Parent surfaces the error toast; the modal stays open for retry.
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">
          {opportunity ? 'Edit Opportunity' : 'Add New Opportunity'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Opportunity Name *</label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Enterprise Contract Q2"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Company *</label>
            <Input
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Company name"
              className={errors.company ? 'border-red-500' : ''}
            />
            {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Deal Value (₹) *</label>
            <Input
              name="value"
              type="number"
              value={formData.value}
              onChange={handleChange}
              placeholder="50000"
              className={errors.value ? 'border-red-500' : ''}
            />
            {errors.value && <p className="text-red-500 text-sm mt-1">{errors.value}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Probability (%) *</label>
            <Input
              name="probability"
              type="number"
              min="0"
              max="100"
              value={formData.probability}
              onChange={handleChange}
              placeholder="75"
              className={errors.probability ? 'border-red-500' : ''}
            />
            {errors.probability && <p className="text-red-500 text-sm mt-1">{errors.probability}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Stage *</label>
            <Select
              name="stage"
              value={formData.stage}
              onChange={(value) => handleSelectChange('stage', Number(value))}
              options={stageOptions.map((s) => ({ value: s.id, label: s.name }))}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Close Date *</label>
            <Input
              name="closeDate"
              type="date"
              value={formData.closeDate}
              onChange={handleChange}
              className={errors.closeDate ? 'border-red-500' : ''}
            />
            {errors.closeDate && <p className="text-red-500 text-sm mt-1">{errors.closeDate}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Assigned Rep</label>
            <Select
              name="rep"
              value={formData.rep}
              onChange={(value) => handleSelectChange('rep', value)}
              options={[
                { value: '', label: 'Unassigned' },
                ...(reps || []).map((r) => ({ value: r, label: r })),
              ]}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Notes / Description</label>
            <textarea
              name="source"
              rows={2}
              value={formData.source}
              onChange={(e) => handleChange(e)}
              placeholder="Free text saved to the deal's description"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2"
              style={{ borderColor: '#D1D5DB', minHeight: 56 }}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
            >
              {saving ? 'Saving…' : opportunity ? 'Update Opportunity' : 'Add Opportunity'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
